<template>
  <v-container fluid class="pa-0 pa-md-5">
    <v-row class="mb-2">
      <v-col cols="12" md="4">
        <v-card dark class="pa-4 rounded-lg" outlined elevation="2" style="border-left: 5px solid #ffa000">
          <div class="text-overline mb-1 grey--text text--darken-1">Temporäre Verträge</div>
          <div class="d-flex align-center">
            <div class="text-h4 amber--text text--darken-2 font-weight-bold mr-2">{{ totalContractsCount }}</div>
            <v-chip x-small color="amber darken-2" outlined v-if="loading">lade...</v-chip>
          </div>
          <v-icon style="position: absolute; right: 15px; top: 15px; opacity: 0.1; font-size: 60px">mdi-file-clock</v-icon>
        </v-card>
      </v-col>
    </v-row>

    <v-card dark elevation="3" class="mb-6 rounded-lg">
      <v-card-text class="pa-4">
        <v-row align="center" dense>
          <v-col cols="12" sm="6" md="4">
            <v-text-field
              v-model="searchQuery"
              label="Suchen (Name, E-Mail)"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              @input="debounceSearch"
              placeholder="Eingeben...">
            </v-text-field>
          </v-col>

          <v-col v-if="true || userService.hasRight('c_create') || userService.hasRight('c_open_contracts')" cols="12" sm="6" md="4">
            <v-select
              :items="studios"
              label="Studios filtern"
              v-model="studioSelect"
              item-text="name"
              item-value="_id"
              multiple
              outlined
              dense
              hide-details
              small-chips
              deletable-chips
              :disabled="!userService.hasRight('c_create')">
              <template v-slot:selection="{ item, index }">
                <span v-if="index === 0" class="grey--text text-caption font-weight-bold"> {{ studioSelect.length }} Studios </span>
              </template>
            </v-select>
          </v-col>

          <v-col cols="12" md="4" class="d-flex justify-end align-center">
            <v-tooltip v-if="userService.hasRight('c_create') || userService.hasRight('c_open_contracts')" bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="grey darken-1" @click="selectAllStudios" v-bind="attrs" v-on="on" :disabled="!userService.hasRight('c_create')">
                  <v-icon>{{
                    studioSelect.length === studios.length ? "mdi-checkbox-multiple-outline" : "mdi-checkbox-multiple-blank-outline"
                  }}</v-icon>
                </v-btn>
              </template>
              <span>Alle Studios an/abwählen</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon color="primary" @click="reloadAll" v-bind="attrs" v-on="on" :loading="loading">
                  <v-icon>mdi-refresh</v-icon>
                </v-btn>
              </template>
              <span>Liste aktualisieren</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <v-card dark elevation="0" class="mx-auto transparent" color="transparent">
      <v-row v-if="loading && page === 1" class="justify-center py-10">
        <v-progress-circular indeterminate color="amber" size="64"></v-progress-circular>
      </v-row>

      <v-row v-else-if="openContracts.length === 0" class="justify-center py-10">
        <v-col cols="12" class="text-center grey--text text--lighten-1">
          <v-icon size="80" color="grey darken-2">mdi-check-all</v-icon>
          <div class="text-h6 mt-4">Keine temporären Verträge</div>
          <div class="subtitle-2 grey--text text--darken-1">Liste leer</div>
        </v-col>
      </v-row>

      <div v-else>
        <v-expansion-panels v-model="userContractPanel" dark>
          <v-expansion-panel v-for="(user, i) of openContracts" :key="user._id" class="mb-3 rounded-lg elevation-4">
            <v-expansion-panel-header :color="getExpansionColorByDate(user.newestCreatedAt)">
              <template v-slot:default="{ open }">
                <v-row no-gutters align="center">
                  <v-col cols="12" sm="5" class="d-flex align-center">
                    <v-avatar size="32" color="rgba(0,0,0,0.2)" class="mr-3">
                      <span class="white--text text-caption font-weight-bold">{{ i + 1 }}</span>
                    </v-avatar>
                    <span class="text-h6 font-weight-bold mr-2 white--text">{{ user.vorname }} {{ user.nachname }}</span>
                  </v-col>

                  <v-col cols="6" sm="4" class="text-caption white--text d-flex align-center">
                    <v-icon x-small left color="white">mdi-map-marker</v-icon>
                    <span class="text-truncate">{{ getStudiosOfContracts(user.contracts) }}</span>
                  </v-col>

                  <v-col cols="6" sm="3" class="text-right text-caption white--text font-weight-bold">
                    {{ $moment(user.newestCreatedAt).format("DD.MM.YY HH:mm") }} Uhr
                  </v-col>
                </v-row>
              </template>
            </v-expansion-panel-header>

            <v-expansion-panel-content class="grey darken-4 pt-4">
              <v-expansion-panels v-model="openContractPanel" accordion flat>
                <v-expansion-panel
                  v-for="(contract, j) of user.contracts"
                  :key="contract._id"
                  class="transparent"
                  :disabled="contract.status.toString() !== 'temporary'">
                  <v-expansion-panel-header class="pl-0 py-2">
                    <div class="d-flex align-center">
                      <v-icon small class="mr-3" color="amber">mdi-file-clock</v-icon>
                      <span class="subtitle-1">{{ getNameOfContract(contract.contractId) }}</span>
                      <v-spacer></v-spacer>
                      <span class="text-caption grey--text">Erstellt: {{ $moment(contract.createdAt).format("DD.MM.YY HH:mm") }}</span>
                    </div>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <v-row dense>
                      <v-col cols="12" md="6">
                        <v-card outlined color="grey darken-3" class="pa-3 fill-height">
                          <div class="subtitle-2 grey--text mb-2">Details</div>

                          <div v-if="contract.customer.contractCreatorInfos" class="mb-2">
                            <v-icon x-small left>mdi-account-edit</v-icon>
                            <span class="caption"
                              >Ersteller: {{ contract.customer.contractCreatorInfos.firstName }}
                              {{ contract.customer.contractCreatorInfos.lastName }}</span
                            >
                            <div class="caption ml-6 grey--text">{{ contract.customer.contractCreatorInfos.email }}</div>
                          </div>

                          <div v-if="contract.category.isTakeover && contract.category.takeover" class="mb-2 red--text text--lighten-2">
                            <v-icon x-small left color="red lighten-2">mdi-account-switch</v-icon>
                            <span class="caption font-weight-bold"
                              >Übernahme von: {{ contract.category.takeover.firstName }} {{ contract.category.takeover.lastName }}</span
                            >
                            <v-img class="mt-1 bg-white rounded" max-width="150" :src="contract.category.takeover.signature"></v-img>
                          </div>

                          <div v-if="contract.category.isAffiliate && contract.category.affiliateInfos" class="mb-2 green--text text--lighten-2">
                            <v-icon x-small left color="green lighten-2">mdi-account-heart</v-icon>
                            <span class="caption"
                              >Empfohlen von: {{ contract.category.affiliateInfos.firstName }} {{ contract.category.affiliateInfos.lastName }}</span
                            >
                          </div>

                          <div v-if="contract.hasPurchasedPersonalTraining" class="mb-2">
                            <v-chip x-small color="blue" label>Personal Training</v-chip>
                          </div>

                          <div v-if="hasImages(contract)" class="mt-3">
                            <div class="caption grey--text mb-1">Anhänge:</div>
                            <div class="d-flex flex-wrap">
                              <template v-for="(img, idx) in getAllImages(contract)">
                                <v-btn :key="idx" x-small color="grey darken-2" class="mr-2 mb-2" @click="downloadContractImageWrapper(img)">
                                  <v-icon left x-small>mdi-paperclip</v-icon> Bild {{ idx + 1 }}
                                </v-btn>
                              </template>
                            </div>
                          </div>
                        </v-card>
                      </v-col>

                      <v-col cols="12" md="6">
                        <v-card outlined color="grey darken-3" class="pa-3 fill-height">
                          <div class="subtitle-2 grey--text mb-2">Historie & Aktion</div>

                          <div class="contract-history overflow-y-auto mb-3" style="max-height: 100px">
                            <div v-for="(comment, cIdx) of contract.comments" :key="cIdx" class="caption mb-1">
                              <div v-if="comment.date && comment.comment">
                                <span class="grey--text">{{ $moment(comment.date).format("DD.MM HH:mm") }}:</span>
                                <span> {{ comment.comment }}</span>
                              </div>
                            </div>
                          </div>

                          <v-textarea
                            v-model="contract.comment"
                            rows="3"
                            label="Neuer Kommentar"
                            dense
                            outlined
                            hide-details
                            class="mb-3"
                            background-color="grey darken-4">
                          </v-textarea>

                          <v-btn
                            block
                            small
                            color="primary"
                            class="mb-3"
                            @click="updateContract(user._id, contract.index, contract.comment, 'temporary', false, false)">
                            Nur Kommentar speichern
                          </v-btn>

                          <v-btn block color="success" @click="updateContract(user._id, contract.index, contract.comment, 'created', false)">
                            <v-icon left>mdi-send</v-icon> Einreichen (Bestätigen)
                          </v-btn>
                        </v-card>
                      </v-col>
                    </v-row>

                    <v-divider class="my-3"></v-divider>

                    <v-row>
                      <v-col cols="12" class="d-flex justify-end">
                        <v-btn
                          v-if="userService.hasRight('c_delete_temporary')"
                          icon
                          color="red lighten-2"
                          @click="visibleDeleteDialogIndex = user._id + '-' + contract.index">
                          <v-icon>mdi-delete</v-icon>
                        </v-btn>

                        <v-btn
                          icon
                          @click="printUserContract(user._id, contract.index, getNameOfContract(contract.contractId))"
                          color="blue lighten-2"
                          class="mr-2">
                          <v-icon>mdi-printer</v-icon>
                        </v-btn>
                        <v-btn
                          icon
                          @click="$router.push('/dashboard/contract/edit/' + user._id + '/' + contract.index)"
                          color="orange lighten-2"
                          class="mr-2">
                          <v-icon>mdi-pencil</v-icon>
                        </v-btn>
                      </v-col>
                    </v-row>

                    <v-expand-transition>
                      <div v-if="userContractPanel === i && openContractPanel === j" class="mt-3">
                        <contract-pdf-preview
                          :key="[user._id, contract._id, contract.index].join(':')"
                          :user-id="user._id"
                          :contract-index="contract.index"
                          :is-app="!!isApp"
                          @open="printUserContract(user._id, contract.index, getNameOfContract(contract.contractId))" />
                      </div>
                    </v-expand-transition>

                    <delete-dialog
                      @onDelete="deleteContract(user._id, contract.index)"
                      @onCancel="visibleDeleteDialogIndex = null"
                      :dialog-title="'Vertrag löschen'"
                      :dialog-visible="visibleDeleteDialogIndex === user._id + '-' + contract.index"
                      :itemName="'Vertrag'">
                    </delete-dialog>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <div class="text-center mt-6" v-if="page < totalPages">
          <v-btn :loading="loadingMore" @click="loadMore" color="amber darken-2" rounded outlined large dark>
            <v-icon left>mdi-chevron-down</v-icon> Mehr laden ({{ openContracts.length }} / {{ totalContractsCount }})
          </v-btn>
        </div>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import api from "../../../api"
import userService from "../../../services/userService"
import deleteDialog from "../../../components/tools/deleteDialog.vue"
import contractPdfPreview from "./components/contractPdfPreview.vue"

export default {
  components: { contractPdfPreview, deleteDialog },
  data() {
    return {
      // Logic
      userService,
      openContractPanel: null,
      userContractPanel: null,
      visibleDeleteDialogIndex: null,

      // Pagination & Filter
      page: 1,
      limit: 20,
      totalPages: 1,
      totalContractsCount: 0,
      searchQuery: "",
      loading: true,
      loadingMore: false,
      debounceTimeout: null,

      // Data
      contracts: [], // Shells
      openContracts: [],
      studios: [],
      studioSelect: [],
    }
  },

  async created() {
    // 1. Load basic data parallel
    try {
      const [studios, userStudio] = await Promise.all([api.fetchStudios(), api.getUserStudio()])
      this.studios = studios

      // Initial Studio Selection
      if (userStudio && userStudio.studio) {
        this.studioSelect = [userStudio.studio._id]
      } else if (userService.hasRight("c_create")) {
        // Admins select logic
      }

      // Load Shells
      this.contracts = await api.fetchContracts()

      // Load Data
      this.fetchData(true)
    } catch (e) {
      console.error("Init failed", e)
    }
  },

  computed: {
    isApp() {
      return window.flutter_inappwebview || false
    },
  },

  methods: {
    // --- DATA LOADING ---
    async fetchData(reset = false) {
      if (reset) {
        this.page = 1
        this.loading = true
        this.openContracts = []
      } else {
        this.loadingMore = true
      }

      try {
        const params = {
          page: this.page,
          limit: this.limit,
          studios: this.studioSelect,
          search: this.searchQuery,
        }

        // Reuse the SAME optimized endpoint with status='temporary'
        const response = await api.fetchContractsByStatus("temporary", params)

        let newUsers = []
        if (response && response.users) {
          newUsers = response.users
          this.totalContractsCount = response.total
          this.totalPages = response.pages
        } else if (Array.isArray(response)) {
          // Fallback old API
          newUsers = response
          this.totalContractsCount = newUsers.length
          this.totalPages = 1
        }

        // Post-Processing
        newUsers.forEach((u) => {
          if (u.contracts) {
            u.contracts.forEach((c) => {
              c.comment = ""
            })
            // Sort contracts by creation
            u.contracts.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

            // Ensure newestCreatedAt is set for coloring
            if (!u.newestCreatedAt && u.contracts.length > 0) u.newestCreatedAt = u.contracts[0].createdAt
          }
        })

        if (reset) {
          this.openContracts = newUsers
        } else {
          this.openContracts = [...this.openContracts, ...newUsers]
        }
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Laden.")
      } finally {
        this.loading = false
        this.loadingMore = false
      }
    },

    reloadAll() {
      this.fetchData(true)
    },

    loadMore() {
      if (this.page < this.totalPages) {
        this.page++
        this.fetchData(false)
      }
    },

    debounceSearch() {
      if (this.debounceTimeout) clearTimeout(this.debounceTimeout)
      this.debounceTimeout = setTimeout(() => {
        this.fetchData(true)
      }, 500)
    },

    // --- ACTIONS ---
    updateContract(userId, index, comment, status, externallySaved = false, showConfirm = true) {
      if (!showConfirm || confirm(status === "created" ? "Vertrag wirklich einreichen?" : "Speichern?")) {
        api
          .updateContractStatus({
            status,
            userId,
            index,
            comment,
            externallySaved,
          })
          .then(() => {
            this.$toast.success(status === "created" ? "Vertrag eingereicht" : "Gespeichert")
            this.fetchData(true) // Reload list
          })
          .catch(() => {
            this.$toast.error("Fehler bei Aktualisierung")
          })
      }
    },

    deleteContract(customerId, contractIndex) {
      // WICHTIG: Nutzt die spezielle deleteTemporaryContract Methode
      api
        .deleteTemporaryContract({ customerId, contractIndex })
        .then(() => {
          this.$toast.success("Vertrag gelöscht")
          this.visibleDeleteDialogIndex = null
          this.fetchData(true)
        })
        .catch(() => this.$toast.error("Fehler beim Löschen"))
    },

    // --- UTILS ---
    selectAllStudios() {
      if (this.studioSelect.length === this.studios.length) {
        this.studioSelect = []
      } else {
        this.studioSelect = this.studios.map((s) => s._id)
      }
    },

    getExpansionColorByDate(date) {
      const daysDiff = this.$moment().diff(this.$moment(date), "days")
      if (daysDiff <= 3) return "default"
      if (daysDiff <= 5) return "orange"
      return "red"
    },

    getNameOfContract(contractId) {
      let contract = this.contracts.find((e) => e._id === contractId)
      return contract?.internalTitle || "Vertrag"
    },

    getNameOfStudio(studioId) {
      let studio = this.studios.find((s) => s._id === studioId)
      return studio ? studio.name : "Unbekannt"
    },

    getStudiosOfContracts(contracts) {
      if (!contracts) return ""
      let studios = contracts.map((e) => e.studio._id)
      studios = studios.map((e) => this.getNameOfStudio(e))
      return [...new Set(studios)].join(", ")
    },

    // Images
    hasImages(contract) {
      return (
        (contract.customer.additionalImage && contract.customer.additionalImage.length > 0) ||
        (contract.customer.additionalImages && contract.customer.additionalImages.length > 0)
      )
    },
    getAllImages(contract) {
      let imgs = []
      if (contract.customer.additionalImage) imgs = [...imgs, ...contract.customer.additionalImage]
      if (contract.customer.additionalImages) imgs = [...imgs, ...contract.customer.additionalImages.map((i) => i.src)]
      return imgs
    },
    downloadContractImageWrapper(imgSrc) {
      api.downloadContractImageData(imgSrc, "Anhang")
    },
    printUserContract(userId, index, title) {
      api.printUserContract(userId, index, title)
    },
  },

  watch: {
    studioSelect(val) {
      this.fetchData(true)
    },
    userContractPanel() {
      this.openContractPanel = undefined
    },
  },
}
</script>

<style scoped>
.contract-history,
.contract-history * {
  -webkit-touch-callout: default;
  -webkit-user-select: text;
  user-select: text;
}
.transparent {
  background-color: transparent !important;
}
.border-white {
  border: 1px solid rgba(255, 255, 255, 0.5);
}
</style>
